import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The calendar journey's small shared pieces: the dark and outline pill buttons, status
 * badges and icon tiles from the Figma, so every screen in the journey matches.
 */

type PillProps = ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean; size?: "md" | "lg" };

const pillBase =
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:pointer-events-none";

export const DarkPill = forwardRef<HTMLButtonElement, PillProps>(function DarkPill(
  { className, loading, size = "md", children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={cn(
        pillBase,
        "bg-foreground text-white hover:bg-foreground/90 disabled:bg-foreground/10 disabled:text-foreground/35",
        size === "lg" ? "h-12 px-6 text-[15px]" : "h-11 px-5 text-[15px]",
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
});

export const OutlinePill = forwardRef<HTMLButtonElement, PillProps>(function OutlinePill(
  { className, loading, size = "md", children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={cn(
        pillBase,
        "border border-foreground/15 bg-white text-foreground hover:bg-foreground/[0.03] disabled:opacity-50",
        size === "lg" ? "h-12 px-6 text-[15px]" : "h-11 px-5 text-[15px]",
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
});

const BADGE_TONES = {
  green: "bg-[#e2efe4] text-[#2f6b45]",
  amber: "bg-[#fbf1d6] text-[#86621a] ring-1 ring-inset ring-[#ecd28c]",
  red: "bg-[#fbe6e6] text-[#ad3640]",
  violet: "bg-primary/10 text-primary",
  muted: "bg-foreground/[0.06] text-muted-foreground",
} as const;

export function Badge({
  tone,
  icon,
  children,
  className,
}: {
  tone: keyof typeof BADGE_TONES;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium",
        BADGE_TONES[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

const TILE_TONES = {
  violet: "bg-primary/10 text-primary",
  green: "bg-[#e2efe4] text-[#2f6b45]",
  red: "bg-[#fbe6e6] text-[#ad3640]",
  muted: "bg-foreground/[0.05] text-muted-foreground",
} as const;

export function IconTile({
  tone = "violet",
  size = "md",
  children,
  className,
}: {
  tone?: keyof typeof TILE_TONES;
  size?: "sm" | "md" | "lg";
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center",
        size === "sm" && "h-8 w-8 rounded-lg",
        size === "md" && "h-9 w-9 rounded-xl",
        size === "lg" && "h-12 w-12 rounded-2xl",
        TILE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** The lavender "SCHEDULING · <step>" card that keeps the step in view through connecting. */
export function StepContextCard({ label, text, className }: { label: string; text: string; className?: string }) {
  return (
    <div className={cn("flex items-center gap-3 rounded-2xl bg-primary/[0.09] px-4 py-3", className)}>
      <span aria-hidden className="h-[18px] w-[18px] shrink-0 rounded-full border-[1.5px] border-foreground/40" />
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">{label}</p>
        <p className="text-[15px] leading-snug text-foreground">{text}</p>
      </div>
    </div>
  );
}

/** Shared sizing for the journey's modals: centred card on wider screens, bottom sheet on phones. */
export const calendarDialogClass =
  "z-[101] gap-0 overflow-y-auto border-0 bg-white p-0 shadow-[0_24px_64px_-16px_rgba(47,41,37,0.35)] sm:rounded-[28px] " +
  "max-sm:bottom-0 max-sm:left-0 max-sm:top-auto max-sm:max-h-[94dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-t-[28px] " +
  "max-sm:data-[state=open]:slide-in-from-bottom-8 max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=closed]:slide-out-to-bottom-8 max-sm:data-[state=closed]:slide-out-to-left-0";

export const calendarOverlayClass = "z-[100] bg-[#2f2925]/40 backdrop-blur-[2px]";
